/**
 * VariantPicker — buyer-facing CJ Dropshipping variant chooser.
 *
 * Renders only when the active product has 2+ variants on
 * `productDetail.variants` (the listing fetch omits the field for
 * single-variant and non-CJ listings, so its presence is the render gate).
 *
 * Layout heuristic:
 *   - If every variant shares the same `key` (e.g. all "Color"), render a
 *     single labeled row of clickable swatches.
 *   - Otherwise fall back to a labeled `<select>` dropdown listing each
 *     variant by `name` (which the server falls back to `variantStandard`
 *     for, then empty string).
 *
 * Tailwind only — no new SCSS per project convention.
 * The <select> reuses the existing `form-select` SCSS class for consistency.
 */

import { useProduct } from '../../contexts';
import Currency from '../currency';
import FormatPrice from '../format-price';
import type { ProductVariant } from '../../contexts/ProductProvider';

const PICKER_LABEL = 'Choose an option';

/** Called with two or more variants (the picker hides itself below that). */
function variantsShareKey(variants: ProductVariant[]): string | null {
  const first = variants[0].key;
  if (!first) return null;
  return variants.every((v) => v.key === first) ? first : null;
}

function variantLabel(variant: ProductVariant): string {
  if (variant.name && variant.name.trim() !== '') return variant.name;
  if (variant.standard && variant.standard.trim() !== '') return variant.standard;
  return variant.vid;
}

/**
 * Find the longest common word-prefix shared by ALL labels and strip it.
 * Returns originals if there is no common prefix, or stripping would leave
 * any label empty. Always given two or more labels.
 */
function stripCommonPrefix(labels: string[]): string[] {
  const words0 = labels[0].split(/\s+/);
  let prefixLen = words0.length;

  for (let i = 1; i < labels.length; i++) {
    const wordsI = labels[i].split(/\s+/);
    let match = 0;
    while (match < prefixLen && match < wordsI.length && words0[match] === wordsI[match]) {
      match++;
    }
    prefixLen = match;
  }

  if (prefixLen === 0) return labels;

  const stripped = labels.map((label) => {
    const words = label.split(/\s+/);
    return words.slice(prefixLen).join(' ').trim();
  });

  if (stripped.some((s) => s === '')) return labels;
  return stripped;
}

const OPTION_STYLE = { color: '#1d2627', backgroundColor: '#eee' };

export const VariantPicker: React.FC = () => {
  const { productDetail, selectedVariantId, setSelectedVariantId } = useProduct();
  const variants = productDetail?.variants;

  if (!variants || variants.length < 2) return null;

  const sharedKey = variantsShareKey(variants);

  const rawLabels = variants.map(variantLabel);
  const shortLabels = stripCommonPrefix(rawLabels);

  return (
    <div
      data-testid="variant-picker"
      className="py-[8px]"
    >
      <div className="text-base font-semibold mb-[8px]">
        {sharedKey ? sharedKey : PICKER_LABEL}
      </div>

      {sharedKey ? (
        <div className="flex flex-wrap gap-[8px]">
          {variants.map((variant, i) => {
            const isSelected = variant.vid === selectedVariantId;
            return (
              <button
                key={variant.vid}
                type="button"
                onClick={() => setSelectedVariantId(variant.vid)}
                aria-pressed={isSelected}
                data-testid={`variant-swatch-${variant.vid}`}
                className={
                  'rounded-[20px] px-[20px] h-[40px] text-base transition-colors ' +
                  (isSelected
                    ? 'border-2 border-sw-yellow bg-sw-yellow/20 font-semibold'
                    : 'border border-sw-yellow bg-transparent hover:bg-[rgba(255,255,255,0.1)]')
                }
              >
                {shortLabels[i]}
                <span className="ml-[8px] text-xs opacity-70">
                  {Currency(variant.price, FormatPrice)}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <select
          data-testid="variant-dropdown"
          value={selectedVariantId ?? ''}
          // Every option carries a vid, so the value is never empty.
          onChange={(e) => setSelectedVariantId(e.target.value)}
          className="form-select w-full"
        >
          {variants.map((variant, i) => (
            <option key={variant.vid} value={variant.vid} style={OPTION_STYLE}>
              {shortLabels[i]} — {Currency(variant.price, undefined)}
            </option>
          ))}
        </select>
      )}
    </div>
  );
};

export default VariantPicker;
